'use client';

import { useRef, useEffect, useState } from 'react';
import gsap from 'gsap';
import { cn } from '../../lib/utils.js';

/**
 * Simple FadeIn animation component
 */
export function FadeIn({
  children,
  delay = 0,
  duration = 0.8,
  y = 40,
  x = 0,
  trigger = true,
  className,
  as: Component = 'div',
  onComplete,
  ...props
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || !trigger) return;

    const element = ref.current;

    // Use Intersection Observer for viewport detection
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Animate when element enters viewport
            gsap.fromTo(
              element,
              { opacity: 0, y, x },
              {
                opacity: 1,
                y: 0,
                x: 0,
                duration,
                delay,
                ease: 'power3.out',
                onComplete,
              }
            );
            // Unobserve after animation
            observer.unobserve(element);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% visible
        rootMargin: '0px 0px -100px 0px', // Trigger 100px before bottom
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [trigger, delay, duration, y, x, onComplete]);

  return (
    <Component
      ref={ref}
      className={cn('opacity-0', className)}
      {...props}
    >
      {children}
    </Component>
  );
}

/**
 * FadeIn with staggered children
 */
export function FadeInStagger({
  children,
  stagger = 0.12,
  delay = 0,
  duration = 0.8,
  y = 40,
  className,
  childClassName,
  as: Component = 'div',
  ...props
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;

    const element = ref.current;

    // Use Intersection Observer for viewport detection
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Animate children when element enters viewport
            gsap.fromTo(
              element.children,
              { opacity: 0, y },
              {
                opacity: 1,
                y: 0,
                duration,
                delay,
                stagger,
                ease: 'power3.out',
              }
            );
            // Unobserve after animation
            observer.unobserve(element);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% visible
        rootMargin: '0px 0px -100px 0px', // Trigger 100px before bottom
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [stagger, delay, duration, y]);

  return (
    <Component ref={ref} className={className} {...props}>
      {children}
    </Component>
  );
}

export default FadeIn;
